import { t as uiText } from '../../i18n';
// src/features/files/fileUtils.ts
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { Timestamp, collection, doc, getDoc, setDoc, serverTimestamp, updateDoc, arrayUnion, arrayRemove, increment, query, where, getDocs, limit, deleteDoc, writeBatch, runTransaction } from 'firebase/firestore';
import { storage } from '../../lib/firebase';
import { db } from '../../lib/firebase';
import { updateWorkspaceFileStats } from '../workspace/fileStatsUtils';
import { assertWorkspaceInvariant } from '../../utils/workspaceInvariant';
import { MAX_PERMANENT_FILE_SIZE } from '../threads/threadFileUpload';

export const ALLOWED_ACCEPT = 'image/*,video/*,.mp4,.mov,.webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md';

const ALLOWED_EXTENSIONS = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt', 'md',
  // video
  'mp4', 'mov', 'webm',
]);

const ALLOWED_IMAGE_MIME_PREFIX = 'image/';
const ALLOWED_VIDEO_MIME_PREFIX = 'video/';

export const isAllowedNonImageFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() || '' : '';
  if (!ext) return false;
  if (file.type && file.type.startsWith(ALLOWED_IMAGE_MIME_PREFIX)) return false; // 画像は別系統
  // 動画は添付として許可（ただしブラウザによって file.type が空のことがあるため拡張子でも判定）
  if (file.type && file.type.startsWith(ALLOWED_VIDEO_MIME_PREFIX)) return true;
  return ALLOWED_EXTENSIONS.has(ext);
};

export const isImageFile = (file: File): boolean => {
  return !!file.type && file.type.startsWith(ALLOWED_IMAGE_MIME_PREFIX);
};

export const sanitizeFilename = (name: string): string => {
  // 簡易サニタイズ: 制御文字やパス記号を除去
  return name.replace(/[\u0000-\u001f<>:"/\\|?*]+/g, '').slice(0, 200);
};

export const toHex = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
};

export const computeSHA256 = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', arrayBuffer);
  return toHex(digest);
};

export interface FileRecord {
  id: string; // contentHash を既定とする
  title: string;
  normalizedTitle?: string;
  mimeType: string;
  size: number;
  storagePath: string;
  downloadURL: string;
  thumbnailURL?: string | null;
  thumbnailPath?: string | null;
  contentHash: string;
  createdAt: any;
  createdBy: string;
  updatedAt: any;
  updatedBy: string;
  referenceCount?: number;
  attachedBoardIds?: string[];
  attachedThreadRefs?: AttachedThreadRef[];
}

// attachedThreadRefs は旧形式 {threadId, postId} と新形式 {threadId, targetType, targetId} を許容する
export type AttachedThreadRef =
  | { threadId: string; postId: string } // legacy (post only)
  | { threadId: string; targetType: 'post' | 'comment'; targetId: string };

export interface RegisterFileParams {
  workspaceId: string;
  boardId: string;
  userId: string;
  file: File;
  onProgress?: (p: { bytesTransferred: number; totalBytes: number; ratio: number }) => void;
}

export interface RegisterThreadFileParams {
  workspaceId: string;
  threadId: string;
  targetType: 'post' | 'comment';
  targetId: string;
  userId: string;
  file: File;
  onProgress?: (p: { bytesTransferred: number; totalBytes: number; ratio: number }) => void;
}

export const ensureFilesCollection = (workspaceId: string) => {
  return collection(db, `workspaces/${workspaceId}/files`);
};

export const uploadFileToStorage = async (
  workspaceId: string,
  fileId: string,
  file: File,
  opts?: { onProgress?: (p: { bytesTransferred: number; totalBytes: number; ratio: number }) => void }
): Promise<{ downloadURL: string; storagePath: string }> => {
  const sanitized = sanitizeFilename(file.name);
  // Storage パス: {workspaceId}/files/{fileId}/{sanitizedFilename}
  const storagePath = `${workspaceId}/files/${fileId}/${sanitized}`;
  const storageRef = ref(storage, storagePath);
  const task = uploadBytesResumable(storageRef, file, { contentType: file.type || undefined });
  const snapshot = await new Promise<typeof task.snapshot>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        try {
          const ratio = snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0;
          opts?.onProgress?.({ bytesTransferred: snap.bytesTransferred, totalBytes: snap.totalBytes, ratio });
        } catch {}
      },
      (err) => reject(err),
      () => resolve(task.snapshot)
    );
  });
  const downloadURL = await getDownloadURL(snapshot.ref);
  return { downloadURL, storagePath };
};

export const uploadThumbnailToStorage = async (
  workspaceId: string,
  fileId: string,
  blob: Blob
): Promise<{ thumbnailURL: string; thumbnailPath: string }> => {
  const thumbnailPath = `${workspaceId}/files/${fileId}/thumb.jpg`;
  const storageRef = ref(storage, thumbnailPath);
  const snapshot = await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
  const thumbnailURL = await getDownloadURL(snapshot.ref);
  return { thumbnailURL, thumbnailPath };
};

const normalizeTitle = (title: string): string => title.trim().toLowerCase();

export const findFileByContentHash = async (workspaceId: string, contentHash: string): Promise<FileRecord | null> => {
  // まず docId 直指定（既存互換）
  const col = ensureFilesCollection(workspaceId);
  const byIdRef = doc(col, contentHash);
  const byIdSnap = await getDoc(byIdRef);
  if (byIdSnap.exists()) return byIdSnap.data() as FileRecord;

  // 次に contentHash フィールド検索
  const q = query(col, where('contentHash', '==', contentHash), limit(1));
  const qs = await getDocs(q);
  if (!qs.empty) return qs.docs[0].data() as FileRecord;
  return null;
};

export const findFileByNormalizedTitle = async (workspaceId: string, normalized: string): Promise<FileRecord | null> => {
  const col = ensureFilesCollection(workspaceId);
  const q = query(col, where('normalizedTitle', '==', normalized), limit(1));
  const qs = await getDocs(q);
  if (!qs.empty) return qs.docs[0].data() as FileRecord;
  return null;
};

export interface DuplicateCheckResult {
  byHash: FileRecord | null;
  byTitle: FileRecord | null;
  contentHash: string;
  normalizedTitle: string;
}

export const checkDuplicates = async (workspaceId: string, file: File): Promise<DuplicateCheckResult> => {
  const contentHash = await computeSHA256(file);
  const normalizedTitle = normalizeTitle(sanitizeFilename(file.name));

  const [byHash, byTitle] = await Promise.all([
    findFileByContentHash(workspaceId, contentHash),
    findFileByNormalizedTitle(workspaceId, normalizedTitle),
  ]);

  return { byHash, byTitle, contentHash, normalizedTitle };
};

export const registerOrReuseFile = async ({ workspaceId, boardId, userId, file, onProgress }: RegisterFileParams): Promise<FileRecord> => {
  assertWorkspaceInvariant(workspaceId, { source: 'files.registerOrReuseFile' });
  const contentHash = await computeSHA256(file);
  const filesCol = ensureFilesCollection(workspaceId);

  // docId=hash 互換 + contentHash フィールド検索
  const existing = await findFileByContentHash(workspaceId, contentHash);

  if (existing) {
    const fileDocRef = doc(filesCol, existing.id);
    await updateDoc(fileDocRef, {
      updatedAt: serverTimestamp(),
      updatedBy: userId,
      referenceCount: increment(1),
      attachedBoardIds: arrayUnion(boardId)
    });
    const returnRecord = { ...existing, referenceCount: (existing.referenceCount || 0) + 1, attachedBoardIds: [ ...(existing.attachedBoardIds || []), boardId ] };

    return returnRecord;
  }

  const fileId = contentHash; // 新規は hash を docId に（後方互換）
  const isVideo = !!file?.type && file.type.startsWith('video/');

  const thumbPromise = (async () => {
    if (!isVideo) return null;
    try {
      const { generateVideoThumbnail } = await import('../../utils/videoThumbnail');
      const r = await generateVideoThumbnail(file, { maxWidth: 480, atSeconds: 5.0 });
      return r;
    } catch (e: any) {
      return null;
    }
  })();

  const { downloadURL, storagePath } = await uploadFileToStorage(workspaceId, fileId, file, {
    onProgress: onProgress ? (p) => onProgress(p) : undefined
  });

  const thumb = await thumbPromise;
  let thumbnailURL: string | null = null;
  let thumbnailPath: string | null = null;
  if (thumb?.blob) {
    try {
      const t = await uploadThumbnailToStorage(workspaceId, fileId, thumb.blob);
      thumbnailURL = t.thumbnailURL;
      thumbnailPath = t.thumbnailPath;
    } catch (e: any) {
    }
  }

  const record: FileRecord = {
    id: fileId,
    title: sanitizeFilename(file.name),
    normalizedTitle: normalizeTitle(sanitizeFilename(file.name)),
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    storagePath,
    downloadURL,
    thumbnailURL,
    thumbnailPath,
    contentHash,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    referenceCount: 1,
    attachedBoardIds: [boardId]
  };

  const fileDocRef = doc(filesCol, fileId);
  await setDoc(fileDocRef, record);

  // 新規ファイル作成時のワークスペース統計更新
  try {
    await updateWorkspaceFileStats({
      workspaceId,
      sizeDelta: file.size,
      countDelta: 1
    });
  } catch (error) {
    console.warn('ワークスペースファイル統計の更新に失敗:', error);
    // 統計更新の失敗はファイル作成自体には影響させない
  }

  return record;
};

/**
 * スレッド添付（永久）向けのファイル登録/再利用
 * - docId は contentHash（ボード添付と同じ）
 * - 参照は attachedThreadRefs に追記
 * - 既存ファイルは再アップロードせず参照だけ追加
 */
export const registerOrReuseFileForThread = async ({
  workspaceId,
  threadId,
  targetType,
  targetId,
  userId,
  file,
  onProgress,
}: RegisterThreadFileParams): Promise<FileRecord> => {
  assertWorkspaceInvariant(workspaceId, { source: 'files.registerOrReuseFileForThread' });
  if (file.size > MAX_PERMANENT_FILE_SIZE) {
    throw new Error(
      uiText("Permanent files are limited to 100 MB. Attach as a temporary file to allow up to 1 GB.")
    );
  }
  const contentHash = await computeSHA256(file);
  const filesCol = ensureFilesCollection(workspaceId);

  const existing = await findFileByContentHash(workspaceId, contentHash);
  if (existing) {
    const fileDocRef = doc(filesCol, existing.id);
    await updateDoc(fileDocRef, {
      updatedAt: serverTimestamp(),
      updatedBy: userId,
      referenceCount: increment(1),
      attachedThreadRefs: arrayUnion({ threadId, targetType, targetId }),
    } as any);

    const returnRecord: FileRecord = {
      ...existing,
      referenceCount: (existing.referenceCount || 0) + 1,
      attachedThreadRefs: [...(existing.attachedThreadRefs || []), { threadId, targetType, targetId }],
    };
    return returnRecord;
  }

  const fileId = contentHash;
  const { downloadURL, storagePath } = await uploadFileToStorage(workspaceId, fileId, file, {
    onProgress: onProgress ? (p) => onProgress(p) : undefined,
  });

  const record: FileRecord = {
    id: fileId,
    title: sanitizeFilename(file.name),
    normalizedTitle: normalizeTitle(sanitizeFilename(file.name)),
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    storagePath,
    downloadURL,
    contentHash,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    referenceCount: 1,
    attachedBoardIds: [],
    attachedThreadRefs: [{ threadId, targetType, targetId }],
  };

  await setDoc(doc(filesCol, fileId), record);

  try {
    await updateWorkspaceFileStats({
      workspaceId,
      sizeDelta: file.size,
      countDelta: 1,
    });
  } catch (error) {
    console.warn('ワークスペースファイル統計の更新に失敗:', error);
  }

  return record;
};

export interface DetachFileParams {
  workspaceId: string;
  fileId: string;
  boardId: string;
  userId: string;
}

/**
 * ボードからファイル参照を解除し、attachedBoardIdsから当該boardIdを除去する
 * 注意: 現在はStorageからの物理削除は行わない
 */
export const detachFileFromBoard = async ({ workspaceId, fileId, boardId, userId }: DetachFileParams): Promise<void> => {
  assertWorkspaceInvariant(workspaceId, { source: 'files.detachFileFromBoard' });
  const filesCol = ensureFilesCollection(workspaceId);
  const fileDocRef = doc(filesCol, fileId);
  const snap = await getDoc(fileDocRef);
  if (!snap.exists()) return;

  const data = snap.data() as Partial<FileRecord>;
  const currentCount = typeof data.referenceCount === 'number' ? data.referenceCount : 1;
  const nextCount = Math.max(0, currentCount - 1);
  const fileSize = data.size || 0;

  // arrayRemoveで boardId を除去し、参照カウントを減算（0未満防止）
  await updateDoc(fileDocRef, {
    attachedBoardIds: arrayRemove(boardId),
    referenceCount: nextCount,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  } as any);

  // TODO: 実際のStorageファイル削除機能実装時に統計更新を有効化
  // 現在は参照カウントのみ減らし、Storageファイルは残すため統計は更新しない
  // if (nextCount === 0) {
  //   try {
  //     await updateWorkspaceFileStats({
  //       workspaceId,
  //       sizeDelta: -fileSize,
  //       countDelta: -1
  //     });
  //   } catch (error) {
  //     console.warn('ワークスペースファイル統計の更新に失敗:', error);
  //   }
  // }
};

export interface AttachExistingParams {
  workspaceId: string;
  fileId: string; // 既存ドキュメントID
  boardId: string;
  userId: string;
}

export const attachExistingFile = async ({ workspaceId, fileId, boardId, userId }: AttachExistingParams): Promise<FileRecord> => {
  const col = ensureFilesCollection(workspaceId);
  const refDoc = doc(col, fileId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error('File not found');
  await updateDoc(refDoc, {
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    referenceCount: increment(1),
    attachedBoardIds: arrayUnion(boardId),
  } as any);
  const updated = await getDoc(refDoc);
  return updated.data() as FileRecord;
};

export interface AttachExistingThreadParams {
  workspaceId: string;
  fileId: string;
  threadId: string;
  targetType: 'post' | 'comment';
  targetId: string;
  userId: string;
}

export const attachExistingFileToThread = async ({
  workspaceId,
  fileId,
  threadId,
  targetType,
  targetId,
  userId,
}: AttachExistingThreadParams): Promise<FileRecord> => {
  const col = ensureFilesCollection(workspaceId);
  const refDoc = doc(col, fileId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error('File not found');
  await updateDoc(refDoc, {
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    referenceCount: increment(1),
    attachedThreadRefs: arrayUnion({ threadId, targetType, targetId }),
  } as any);
  const updated = await getDoc(refDoc);
  return updated.data() as FileRecord;
};

export interface OverwriteExistingParams {
  workspaceId: string;
  fileId: string; // 既存ドキュメントID（保持）
  boardId: string;
  userId: string;
  file: File;
}

export const overwriteExistingFile = async ({ workspaceId, fileId, boardId, userId, file }: OverwriteExistingParams): Promise<FileRecord> => {
  assertWorkspaceInvariant(workspaceId, { source: 'files.overwriteExistingFile' });
  const col = ensureFilesCollection(workspaceId);
  const refDoc = doc(col, fileId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error('File not found');
  const current = snap.data() as FileRecord;
  const oldSize = current.size || 0;
  
  // 既存の storagePath に上書きアップロード（URLはパス同一だがtokenは変わる場合あり）
  const storageRef = ref(storage, current.storagePath);
  const snapshot = await uploadBytes(storageRef, file, { contentType: file.type || undefined });
  const downloadURL = await getDownloadURL(snapshot.ref);
  const newHash = await computeSHA256(file);

  await updateDoc(refDoc, {
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    size: file.size,
    mimeType: file.type || current.mimeType,
    downloadURL,
    contentHash: newHash,
    attachedBoardIds: arrayUnion(boardId),
    referenceCount: increment(1),
  } as any);

  // ファイル上書き時のワークスペース統計更新（サイズ差分のみ）
  const sizeDelta = file.size - oldSize;
  if (sizeDelta !== 0) {
    try {
      await updateWorkspaceFileStats({
        workspaceId,
        sizeDelta,
        countDelta: 0 // ファイル数は変わらない
      });
    } catch (error) {
      console.warn('ワークスペースファイル統計の更新に失敗:', error);
      // 統計更新の失敗はファイル上書き自体には影響させない
    }
  }

  const updated = await getDoc(refDoc);
  return updated.data() as FileRecord;
};

export interface OverwriteExistingThreadParams {
  workspaceId: string;
  fileId: string;
  threadId: string;
  targetType: 'post' | 'comment';
  targetId: string;
  userId: string;
  file: File;
}

export const overwriteExistingFileToThread = async ({
  workspaceId,
  fileId,
  threadId,
  targetType,
  targetId,
  userId,
  file,
}: OverwriteExistingThreadParams): Promise<FileRecord> => {
  assertWorkspaceInvariant(workspaceId, { source: 'files.overwriteExistingFileToThread' });
  if (file.size > MAX_PERMANENT_FILE_SIZE) {
    throw new Error(
      uiText("Permanent files are limited to 100 MB. Attach as a temporary file to allow up to 1 GB.")
    );
  }
  const col = ensureFilesCollection(workspaceId);
  const refDoc = doc(col, fileId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error('File not found');
  const current = snap.data() as FileRecord;
  const oldSize = current.size || 0;

  const storageRef = ref(storage, current.storagePath);
  const snapshot = await uploadBytes(storageRef, file, { contentType: file.type || undefined });
  const downloadURL = await getDownloadURL(snapshot.ref);
  const newHash = await computeSHA256(file);

  await updateDoc(refDoc, {
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    size: file.size,
    mimeType: file.type || current.mimeType,
    downloadURL,
    contentHash: newHash,
    attachedThreadRefs: arrayUnion({ threadId, targetType, targetId }),
    referenceCount: increment(1),
  } as any);

  const sizeDelta = file.size - oldSize;
  if (sizeDelta !== 0) {
    try {
      await updateWorkspaceFileStats({
        workspaceId,
        sizeDelta,
        countDelta: 0,
      });
    } catch (error) {
      console.warn('ワークスペースファイル統計の更新に失敗:', error);
    }
  }

  const updated = await getDoc(refDoc);
  return updated.data() as FileRecord;
};

export interface CreateNewWithTitleParams {
  workspaceId: string;
  boardId: string;
  userId: string;
  file: File;
  newTitle: string;
}

export const createNewFileWithTitle = async ({ workspaceId, boardId, userId, file, newTitle }: CreateNewWithTitleParams): Promise<FileRecord> => {
  assertWorkspaceInvariant(workspaceId, { source: 'files.createNewFileWithTitle' });
  const contentHash = await computeSHA256(file);
  const filesCol = ensureFilesCollection(workspaceId);
  // リネーム新規はユニークID採用（contentHashが同一でも別レコードとして保存）
  const fileId = `${contentHash}-${Date.now().toString(36)}`;

  // タイトルユニーク化（同名があれば _1, _2 ... を拡張子の前に付与）
  const proposeUniqueTitle = async (titleInput: string): Promise<string> => {
    const base = sanitizeFilename(titleInput || file.name);
    const idx = base.lastIndexOf('.');
    const basename = idx > 0 ? base.slice(0, idx) : base;
    const ext = idx > 0 ? base.slice(idx) : '';
    let candidate = base;
    let counter = 1;
    while (true) {
      const normalized = normalizeTitle(candidate);
      const exists = await findFileByNormalizedTitle(workspaceId, normalized);
      if (!exists) return candidate;
      candidate = `${basename}_${counter}${ext}`;
      counter += 1;
      if (counter > 99) return `${basename}_${Date.now()}${ext}`; // フォールバック
    }
  };

  const uniqueTitle = await proposeUniqueTitle(newTitle);
  const { downloadURL, storagePath } = await uploadFileToStorage(workspaceId, fileId, new File([file], uniqueTitle, { type: file.type }));
  const record: FileRecord = {
    id: fileId,
    title: uniqueTitle,
    normalizedTitle: normalizeTitle(uniqueTitle),
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    storagePath,
    downloadURL,
    contentHash,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    referenceCount: 1,
    attachedBoardIds: [boardId]
  };
  await setDoc(doc(filesCol, fileId), record);

  // 新規ファイル作成時のワークスペース統計更新
  try {
    await updateWorkspaceFileStats({
      workspaceId,
      sizeDelta: file.size,
      countDelta: 1
    });
  } catch (error) {
    console.warn('ワークスペースファイル統計の更新に失敗:', error);
    // 統計更新の失敗はファイル作成自体には影響させない
  }

  return record;
};

export interface CreateNewWithTitleForThreadParams {
  workspaceId: string;
  threadId: string;
  targetType: 'post' | 'comment';
  targetId: string;
  userId: string;
  file: File;
  newTitle: string;
  onProgress?: (p: { bytesTransferred: number; totalBytes: number; ratio: number }) => void;
}

export const createNewFileWithTitleForThread = async ({
  workspaceId,
  threadId,
  targetType,
  targetId,
  userId,
  file,
  newTitle,
  onProgress,
}: CreateNewWithTitleForThreadParams): Promise<FileRecord> => {
  assertWorkspaceInvariant(workspaceId, { source: 'files.createNewFileWithTitleForThread' });
  if (file.size > MAX_PERMANENT_FILE_SIZE) {
    throw new Error(
      uiText("Permanent files are limited to 100 MB. Attach as a temporary file to allow up to 1 GB.")
    );
  }
  const contentHash = await computeSHA256(file);
  const filesCol = ensureFilesCollection(workspaceId);
  const fileId = `${contentHash}-${Date.now().toString(36)}`;

  const proposeUniqueTitle = async (titleInput: string): Promise<string> => {
    const base = sanitizeFilename(titleInput || file.name);
    const idx = base.lastIndexOf('.');
    const basename = idx > 0 ? base.slice(0, idx) : base;
    const ext = idx > 0 ? base.slice(idx) : '';
    let candidate = base;
    let counter = 1;
    while (true) {
      const normalized = normalizeTitle(candidate);
      const exists = await findFileByNormalizedTitle(workspaceId, normalized);
      if (!exists) return candidate;
      candidate = `${basename}_${counter}${ext}`;
      counter += 1;
      if (counter > 99) return `${basename}_${Date.now()}${ext}`;
    }
  };

  const uniqueTitle = await proposeUniqueTitle(newTitle);
  const { downloadURL, storagePath } = await uploadFileToStorage(
    workspaceId,
    fileId,
    new File([file], uniqueTitle, { type: file.type }),
    { onProgress }
  );

  const record: FileRecord = {
    id: fileId,
    title: uniqueTitle,
    normalizedTitle: normalizeTitle(uniqueTitle),
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    storagePath,
    downloadURL,
    contentHash,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    referenceCount: 1,
    attachedBoardIds: [],
    attachedThreadRefs: [{ threadId, targetType, targetId }],
  };

  await setDoc(doc(filesCol, fileId), record);

  try {
    await updateWorkspaceFileStats({
      workspaceId,
      sizeDelta: file.size,
      countDelta: 1,
    });
  } catch (error) {
    console.warn('ワークスペースファイル統計の更新に失敗:', error);
  }

  return record;
};

/**
 * 複数ファイルの削除処理
 */
export interface DeleteMultipleFilesParams {
  workspaceId: string;
  fileIds: string[];
  userId: string;
}

export interface DeleteFilesResult {
  succeeded: string[];
  failed: Array<{ fileId: string; error: string }>;
  totalSizeDeleted: number;
  totalCountDeleted: number;
}

/**
 * 参照ページに削除予約を設定する（ファイル情報付き）
 */
const setPendingFileRemovals = async (
  workspaceId: string, 
  boardIds: string[], 
  filesToDelete: Array<{ id: string; record: FileRecord }>
): Promise<void> => {
  if (boardIds.length === 0) return;
  
  const batch = writeBatch(db);
  const timestamp = Date.now();
  
  // 削除ファイルの情報を保存
  const deletedFileInfo = filesToDelete.map(f => ({
    fileId: f.id,
    title: f.record.title,
    mimeType: f.record.mimeType,
    size: f.record.size
  }));
  
  boardIds.forEach(boardId => {
    const boardRef = doc(db, `workspaces/${workspaceId}/boards/${boardId}`);
    batch.update(boardRef, {
      [`pendingFileRemovals.${timestamp}`]: {
        removedFileIds: filesToDelete.map(f => f.id),
        deletedFileInfo, // ファイル情報も保存
        timestamp: serverTimestamp(),
        processed: false
      }
    });
  });
  
  await batch.commit();
};

/**
 * ワークスペース統計を更新（ファイル数が0の場合はサイズも0にする）
 */
const updateWorkspaceStatsForDeletion = async (
  workspaceId: string,
  totalSizeDeleted: number,
  totalCountDeleted: number
): Promise<void> => {
  const workspaceRef = doc(db, 'workspaces', workspaceId);
  
  await runTransaction(db, async (transaction) => {
    const workspaceDoc = await transaction.get(workspaceRef);
    
    if (!workspaceDoc.exists()) {
      throw new Error(`Workspace ${workspaceId} not found`);
    }
    
    const currentData = workspaceDoc.data();
    const currentTotalSize = currentData.totalFileSize || 0;
    const currentFileCount = currentData.fileCount || 0;
    
    const newFileCount = Math.max(0, currentFileCount - totalCountDeleted);
    let newTotalSize = Math.max(0, currentTotalSize - totalSizeDeleted);
    
    // ファイル数が0になった場合はサイズも0に設定
    if (newFileCount === 0) {
      newTotalSize = 0;
    }
    
    transaction.update(workspaceRef, {
      totalFileSize: newTotalSize,
      fileCount: newFileCount,
      lastFileSizeUpdate: serverTimestamp(),
    });
  });
};

/**
 * 複数ファイルを削除する（Storage + Firestore + 統計更新 + 予約処理）
 */
export const deleteMultipleFiles = async ({ 
  workspaceId, 
  fileIds, 
  userId 
}: DeleteMultipleFilesParams): Promise<DeleteFilesResult> => {
  assertWorkspaceInvariant(workspaceId, { source: 'files.deleteMultipleFiles' });
  const result: DeleteFilesResult = {
    succeeded: [],
    failed: [],
    totalSizeDeleted: 0,
    totalCountDeleted: 0
  };

  const filesCol = ensureFilesCollection(workspaceId);
  const allReferencedBoardIds = new Set<string>();
  const filesToDelete: Array<{ id: string; record: FileRecord }> = [];
  const threadPostTargets = new Map<string, { threadId: string; postId: string; fileIds: Set<string>; fileNameById: Map<string, string> }>();
  const threadCommentTargets = new Map<string, { threadId: string; commentId: string; fileIds: Set<string>; fileNameById: Map<string, string> }>();

  for (const fileId of fileIds) {
    try {
      const fileDocRef = doc(filesCol, fileId);
      const snap = await getDoc(fileDocRef);

      if (!snap.exists()) {
        result.failed.push({ fileId, error: uiText("File not found") });
        continue;
      }

      const record = snap.data() as FileRecord;
      filesToDelete.push({ id: fileId, record });

      // 参照ページを収集
      if (record.attachedBoardIds) {
        record.attachedBoardIds.forEach(boardId => allReferencedBoardIds.add(boardId));
      }

      // スレッド参照を収集（削除プレースホルダ反映用）
      if (Array.isArray((record as any).attachedThreadRefs)) {
        for (const r of (record as any).attachedThreadRefs as AttachedThreadRef[]) {
          const threadId = String((r as any)?.threadId ?? '');
          if (!threadId) continue;
          const targetType = (r as any)?.targetType === 'comment' ? 'comment' : 'post';
          const targetId = String((r as any)?.targetId ?? (r as any)?.postId ?? '');
          if (!targetId) continue;

          if (targetType === 'comment') {
            const key = `${threadId}/${targetId}`;
            const cur = threadCommentTargets.get(key) ?? {
              threadId,
              commentId: targetId,
              fileIds: new Set<string>(),
              fileNameById: new Map<string, string>(),
            };
            cur.fileIds.add(fileId);
            cur.fileNameById.set(fileId, record.title || uiText("File"));
            threadCommentTargets.set(key, cur);
          } else {
            const key = `${threadId}/${targetId}`;
            const cur = threadPostTargets.get(key) ?? {
              threadId,
              postId: targetId,
              fileIds: new Set<string>(),
              fileNameById: new Map<string, string>(),
            };
            cur.fileIds.add(fileId);
            cur.fileNameById.set(fileId, record.title || uiText("File"));
            threadPostTargets.set(key, cur);
          }
        }
      }
    } catch (error) {
      console.error('🗑️ [DELETE] ファイル情報取得エラー', { fileId, error });
      result.failed.push({ fileId, error: uiText("Failed to get file info") });
    }
  }

  if (filesToDelete.length === 0) {
    return result;
  }

  try {
    await setPendingFileRemovals(
      workspaceId,
      Array.from(allReferencedBoardIds),
      filesToDelete
    );

    // アクティブなエディタがある場合は即座に更新を実行
    try {
      const { updateFileRemovalInActiveYDocs } = await import('./ydocFileRemovalUtils');
      updateFileRemovalInActiveYDocs(
        workspaceId,
        Array.from(allReferencedBoardIds),
        filesToDelete.map(f => f.id),
        filesToDelete.map(f => ({
          fileId: f.id,
          title: f.record.title,
          mimeType: f.record.mimeType,
          size: f.record.size
        }))
      );
    } catch (activeUpdateError) {
      console.warn('🗑️ [DELETE] アクティブエディタ更新エラー（予約処理でカバー）:', activeUpdateError);
    }
  } catch (error) {
    console.error('🗑️ [DELETE] 削除予約設定エラー', error);
    // 予約設定に失敗した場合も削除は続行（エディタ側で手動対応が必要）
  }

  // 3. ファイルを個別に削除
  for (const { id: fileId, record } of filesToDelete) {
    try {
      // Storage から削除
      if (record.storagePath) {
        try {
          const storageRef = ref(storage, record.storagePath);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn('🗑️ [DELETE] Storage削除失敗（続行）', { fileId, error: storageError });
          // Storageの削除失敗はログに残すが処理は続行
        }
      }

      // Firestore から削除
      const fileDocRef = doc(filesCol, fileId);
      await deleteDoc(fileDocRef);

      result.succeeded.push(fileId);
      result.totalSizeDeleted += record.size || 0;
      result.totalCountDeleted += 1;
    } catch (error) {
      console.error('🗑️ [DELETE] ファイル削除エラー', { fileId, error });
      result.failed.push({ fileId, error: uiText("Failed to delete file") });
    }
  }

  // 3.5 スレッド側の添付表示を削除プレースホルダに置換（永久ファイル）
  if (threadPostTargets.size > 0) {
    for (const t of Array.from(threadPostTargets.values())) {
      try {
        const postRef = doc(db, `workspaces/${workspaceId}/threads/${t.threadId}/posts/${t.postId}`);
        const snap = await getDoc(postRef);
        if (!snap.exists()) continue;
        const data = snap.data() as any;
        const attachments = Array.isArray(data.attachments) ? data.attachments : [];
        const next = attachments.map((a: any) => {
          if (!a) return a;
          const id = String(a.id ?? '');
          const type = String(a.attachmentType ?? '');
          if (type === 'permanent' && t.fileIds.has(id)) {
            return {
              ...a,
              isDeleted: true,
              // serverTimestamp は配列要素内で使えないためクライアント時刻を使用
              deletedAt: Timestamp.now(),
              deletedBy: userId,
              downloadURL: '',
            };
          }
          return a;
        });
        await updateDoc(postRef, {
          attachments: next,
          updatedAt: serverTimestamp(),
          updatedBy: userId,
        } as any);
      } catch (e) {
        console.warn('🗑️ [DELETE] スレッド添付反映エラー（続行）', e);
      }
    }
  }

  if (threadCommentTargets.size > 0) {
    for (const t of Array.from(threadCommentTargets.values())) {
      try {
        const commentRef = doc(db, `workspaces/${workspaceId}/threads/${t.threadId}/comments/${t.commentId}`);
        const snap = await getDoc(commentRef);
        if (!snap.exists()) continue;
        const data = snap.data() as any;
        const attachments = Array.isArray(data.attachments) ? data.attachments : [];
        const next = attachments.map((a: any) => {
          if (!a) return a;
          const id = String(a.id ?? '');
          const type = String(a.attachmentType ?? '');
          if (type === 'permanent' && t.fileIds.has(id)) {
            return {
              ...a,
              isDeleted: true,
              // serverTimestamp は配列要素内で使えないためクライアント時刻を使用
              deletedAt: Timestamp.now(),
              deletedBy: userId,
              downloadURL: '',
            };
          }
          return a;
        });
        await updateDoc(commentRef, {
          attachments: next,
          updatedAt: serverTimestamp(),
          updatedBy: userId,
        } as any);
      } catch (e) {
        console.warn('🗑️ [DELETE] コメント添付反映エラー（続行）', e);
      }
    }
  }

  // 4. ワークスペース統計を更新
  if (result.totalCountDeleted > 0) {
    try {
      await updateWorkspaceStatsForDeletion(
        workspaceId,
        result.totalSizeDeleted,
        result.totalCountDeleted
      );
    } catch (error) {
      console.error('🗑️ [DELETE] 統計更新エラー', error);
      // 統計更新の失敗は警告として扱う
    }
  }

  return result;
};

/**
 * 単一ファイルの削除（後方互換性のため）
 */
export interface DeleteFileFromStorageParams {
  workspaceId: string;
  fileId: string;
  userId: string;
}

export const deleteFileFromStorage = async ({ 
  workspaceId, 
  fileId, 
  userId 
}: DeleteFileFromStorageParams): Promise<void> => {
  const result = await deleteMultipleFiles({
    workspaceId,
    fileIds: [fileId],
    userId
  });
  
  if (result.failed.length > 0) {
    throw new Error(result.failed[0].error);
  }
};


